import { ButtonHTMLAttributes, ReactNode, forwardRef, AnchorHTMLAttributes } from 'react';
import Link from 'next/link';
import Icon from './AppIcon';

type BaseButtonProps = {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger' | 'outline';
  size?: 'sm' | 'md' | 'lg';
  isLoading?: boolean;
  icon?: string;
  iconPosition?: 'left' | 'right';
  iconSize?: number;
  children?: ReactNode;
  fullWidth?: boolean;
  disabled?: boolean;
};

type ButtonAsButton = BaseButtonProps &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'disabled'> & {
    href?: never;
  };

type ButtonAsLink = BaseButtonProps &
  Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> & {
    href: string;
  };

export type ButtonProps = ButtonAsButton | ButtonAsLink;

const Button = forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      isLoading = false,
      icon,
      iconPosition = 'left',
      iconSize,
      children,
      fullWidth = false,
      className = '',
      disabled,
      ...props
    },
    ref
  ) => {
    const baseClasses =
      'inline-flex items-center justify-center font-semibold rounded-lg transition-smooth duration-150 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed min-w-[44px] min-h-[44px]';

    const variantClasses = {
      primary:
        'bg-primary text-primary-foreground hover:opacity-90 focus:ring-primary elevation-1 hover-lift',
      secondary:
        'bg-secondary text-secondary-foreground hover:opacity-90 focus:ring-secondary elevation-1 hover-lift',
      ghost: 'bg-transparent text-text-primary hover:bg-muted focus:ring-muted',
      danger:
        'bg-destructive text-destructive-foreground hover:opacity-90 focus:ring-destructive elevation-1 hover-lift',
      outline:
        'bg-surface border-2 border-border text-text-primary hover:bg-muted focus:ring-border',
    };

    const sizeClasses = {
      sm: 'px-3 py-1.5 text-sm',
      md: 'px-6 py-3 text-base',
      lg: 'px-8 py-4 text-lg',
    };

    const iconSizeMap = {
      sm: 16,
      md: 20,
      lg: 24,
    };

    const defaultIconSize = iconSize || iconSizeMap[size];

    const widthClass = fullWidth ? 'w-full' : '';

    const combinedClasses =
      `${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${widthClass} ${className}`.trim();

    const renderIcon = (position: 'left' | 'right') => {
      if (!icon || iconPosition !== position) return null;

      return (
        <Icon data-component-id="src/components/ui/Button.tsx:84:8" data-component-path="src/components/ui/Button.tsx" data-component-line="84" data-component-end-line="88" data-component-file="Button.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22%5Bvar%3Aicon%5D%22%7D"
          name={icon}
          size={defaultIconSize}
          className={position === 'left' ? 'mr-2' : 'ml-2'}
        />
      );
    };

    const renderContent = () => {
      if (isLoading) {
        return (
          <>
            <svg data-component-id="src/components/ui/Button.tsx:96:12" data-component-path="src/components/ui/Button.tsx" data-component-line="96" data-component-end-line="115" data-component-file="Button.tsx" data-component-name="svg" data-component-content="%7B%22elementName%22%3A%22svg%22%2C%22className%22%3A%22animate-spin%20h-5%20w-5%20mr-2%22%7D"
              className="animate-spin h-5 w-5 mr-2"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle data-component-id="src/components/ui/Button.tsx:102:14" data-component-path="src/components/ui/Button.tsx" data-component-line="102" data-component-end-line="109" data-component-file="Button.tsx" data-component-name="circle" data-component-content="%7B%22elementName%22%3A%22circle%22%2C%22className%22%3A%22opacity-25%22%7D"
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path data-component-id="src/components/ui/Button.tsx:110:14" data-component-path="src/components/ui/Button.tsx" data-component-line="110" data-component-end-line="114" data-component-file="Button.tsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%2C%22className%22%3A%22opacity-75%22%7D"
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            <span data-component-id="src/components/ui/Button.tsx:116:12" data-component-path="src/components/ui/Button.tsx" data-component-line="116" data-component-end-line="116" data-component-file="Button.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22textContent%22%3A%22Loading...%22%7D">Loading...</span>
          </>
        );
      }

      return (
        <>
          {renderIcon('left')}
          {children && <span data-component-id="src/components/ui/Button.tsx:124:23" data-component-path="src/components/ui/Button.tsx" data-component-line="124" data-component-end-line="124" data-component-file="Button.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%7D">{children}</span>}
          {renderIcon('right')}
        </>
      );
    };

    // Render as Link if href is provided
    if ('href' in props && props.href) {
      const { href, ...linkProps } = props as ButtonAsLink;
      return (
        <Link data-component-id="src/components/ui/Button.tsx:134:8" data-component-path="src/components/ui/Button.tsx" data-component-line="134" data-component-end-line="141" data-component-file="Button.tsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%2C%22href%22%3A%22%5Bvar%3Ahref%5D%22%2C%22className%22%3A%22%5Bvar%3AcombinedClasses%5D%22%2C%22%5Bspread%5D%22%3A%22true%22%7D"
          href={href}
          ref={ref as React.Ref<HTMLAnchorElement>}
          className={combinedClasses}
          {...linkProps}
        >
          {renderContent()}
        </Link>
      );
    }

    // Render as button
    return (
      <button data-component-id="src/components/ui/Button.tsx:147:6" data-component-path="src/components/ui/Button.tsx" data-component-line="147" data-component-end-line="154" data-component-file="Button.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22%5Bvar%3AcombinedClasses%5D%22%2C%22%5Bspread%5D%22%3A%22true%22%7D"
        ref={ref as React.Ref<HTMLButtonElement>}
        className={combinedClasses}
        disabled={disabled || isLoading}
        {...(props as ButtonHTMLAttributes<HTMLButtonElement>)}
      >
        {renderContent()}
      </button>
    );
  }
);

Button.displayName = 'Button';

export default Button;
